import './ReportCustomer.scss';

import { faArrowDownWideShort, faUser } from '@fortawesome/free-solid-svg-icons';
import { formatDateInEN, formatInReal } from '../../commons/util/HallAppUtil';
import { useEffect, useMemo, useState } from 'react';

import AppCard from '../../commons/cards/AppCard';
import AppLoading from '../../commons/AppLoading';
import { Button } from 'react-bootstrap';
import EmptyState from '../../commons/EmptyState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import HallAppToast from '../../commons/HallAppToast';
import ReportAPI from '../report/ReportAPI';
import ReportFullFilter from './ReportFullFilter';
import ReportHeader from './components/ReportHeader';

/* eslint-disable react-hooks/exhaustive-deps */
function ReportCustomer() {
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [reportCustomers, setReportCustomers] = useState([]);
  const [orderBy, setOrderBy] = useState('VALUE'); // 'VALUE' | 'QTD'

  const reportAPI = new ReportAPI();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [filter, setFilter] = useState({
    startDate: formatDateInEN(firstDayOfMonth),
    endDate: formatDateInEN(new Date()),
  });

  useEffect(() => {
    findReportCustomer();
  }, [filter]);

  async function findReportCustomer() {
    try {
      setIsLoadingSummary(true);
      const response = await reportAPI.getReportCustomer(filter);
      const data = response?.data || [];
      // Ordena inicialmente por valor (desc) sem mutar array original
      const sortedByValue = [...data].sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0));
      setReportCustomers(sortedByValue);
      setOrderBy('VALUE'); // reseta ordenação ao buscar
    } catch (_) {
      HallAppToast.genericError();
      setReportCustomers([]);
    } finally {
      setIsLoadingSummary(false);
    }
  }

  function changeDates(startDate, endDate) {
    let adjustedEndDate;

    if (endDate === null) {
      const start = new Date(startDate);
      adjustedEndDate = new Date(start.getFullYear(), start.getMonth() + 2, 0);
    } else {
      const end = new Date(endDate);
      adjustedEndDate = new Date(end.getFullYear(), end.getMonth() + 2, 0);
    }

    adjustedEndDate.setHours(0, 0, 0, 0);

    setFilter({
      startDate,
      endDate: formatDateInEN(adjustedEndDate),
    });
  }

  function toggleOrder() {
    const nextOrder = orderBy === 'VALUE' ? 'QTD' : 'VALUE';
    const sorted = [...reportCustomers].sort((a, b) => {
      if (nextOrder === 'QTD') {
        return (b.totalVisit || 0) - (a.totalVisit || 0);
      }
      return (b.totalValue || 0) - (a.totalValue || 0);
    });
    setReportCustomers(sorted);
    setOrderBy(nextOrder);
  }

  const orderLabel =
    orderBy === 'VALUE' ? 'Ordenar por quantidade de visitas' : 'Ordenar por valor gasto';

  const CustomerReportCard = ({ report, rank }) => {
    const name = report?.customerName || 'Cliente';
    const visits = report?.totalVisit || 0;
    const total = formatInReal(report?.totalValue || 0);

    // Avatar com iniciais
    const initials = useMemo(() => {
      const parts = String(name).trim().split(' ');
      const first = parts[0]?.[0] || '';
      const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
      return (first + last).toUpperCase();
    }, [name]);

    return (
      <AppCard className="customer-report-card">
        {rank <= 3 && <div className={`customer-report-card__badge badge-${rank}`}>#{rank}</div>}

        <div className="customer-report-card__header">
          <div className="customer-report-card__avatar" aria-hidden="true">
            <FontAwesomeIcon icon={faUser} />
            <span className="customer-report-card__initials">{initials}</span>
          </div>
          <h6 className="customer-report-card__title" title={name}>
            {name}
          </h6>
        </div>

        <div className="customer-report-card__body">
          <div className="customer-report-card__row">
            <span className="customer-report-card__label">Visitas</span>
            <span className="customer-report-card__value">{visits}</span>
          </div>
          <div className="customer-report-card__row">
            <span className="customer-report-card__label">Total gasto</span>
            <span className="customer-report-card__value customer-report-card__value--highlight">
              {total}
            </span>
          </div>
        </div>
      </AppCard>
    );
  };

  return (
    <div className="report-customer">
      <ReportHeader name="Relatório por Cliente" />

      <div className="report-customer__filter">
        <ReportFullFilter changeDates={changeDates} />
      </div>

      <div className="container pb-5">
        {!isLoadingSummary && reportCustomers.length > 0 && (
          <div className="report-customer__controls">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={toggleOrder}
              aria-label={orderLabel}
              className="d-flex align-items-center gap-2"
            >
              <FontAwesomeIcon icon={faArrowDownWideShort} />
              {orderLabel}
            </Button>
          </div>
        )}

        <hr className="my-3" />

        {isLoadingSummary ? (
          <AppLoading />
        ) : reportCustomers.length === 0 ? (
          <EmptyState
            title="Nenhum cliente encontrado"
            description="Não há registros de clientes atendidos no período selecionado. Ajuste as datas para visualizar outros períodos."
            icon={<FontAwesomeIcon icon={faUser} />}
          />
        ) : (
          <div className="row g-4 mt-2">
            {reportCustomers.map((report, index) => (
              <div className="col-12 col-md-6 col-lg-4" key={report.customerId || index}>
                <CustomerReportCard report={report} rank={index + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ height: "calc(50px + 72px)" }}></div>
    </div>
  );
}

export default ReportCustomer;